import React, { ReactElement } from "react";

import Breadcrumb from "components/Breadcrumb";
import EmployeesByLetter from "components/EmployeesByLetter/EmployeesByLetter";
import Link from "components/Link";
import OpenPositionListSection from "components/OpenPositionListSection";
import PageContent from "components/PageContent";
import Section from "components/Section";
import useIsInEditMode from "hooks/useIsInEditMode";
import { Wrapper } from "style/components/Page";
import { addEditAttributes } from "utils/episerver";
import getComponentTypeForContent from "utils/getComponentTypeForContent";
import { getMapsLocationUrl } from "utils/getMapsLocationUrl";

import {
    ContactInfoDetails,
    ContactInformation,
    DetailHeading,
    FindUsLink,
    Heading,
    Introduction,
    OfficeInformation,
    OpeLink,
    StyledPhoneIcon,
    External,
    ContactInfoDetailItem,
    ContactInfoCol,
    Email,
} from "./OfficePage.styled";
import OfficePageProps from "./OfficePageProps";

const OfficePage = ({
    breadcrumb,
    blocks,
    heading,
    introduction,
    theme = "blue",
    editAttributePrefix = "",
    location,
    openPositions,
    phoneNumber,
    email,
    employeesByLetter,
    employeesAnchorName,
    l18n,
}: OfficePageProps): ReactElement => {
    const isInEditMode = useIsInEditMode();
    const hasLocation =
        location?.addressRow1 || location?.postalCode || location?.city;

    const findUsUrl = getMapsLocationUrl(
        15,
        location?.latitude,
        location?.longitude,
        location?.postalCode,
        location?.city,
        location?.addressRow1,
    );
    const showFindUsLink = findUsUrl && l18n?.findUs;

    return (
        <Wrapper id="maincontent">
            {breadcrumb?.items && (
                <Breadcrumb items={breadcrumb.items} theme={theme} />
            )}
            <Section theme={theme} alignItems="center">
                <OfficeInformation>
                    {(isInEditMode || heading) && (
                        <Heading
                            {...addEditAttributes(
                                `${editAttributePrefix}Heading`,
                            )}
                        >
                            {heading}
                        </Heading>
                    )}
                    {(isInEditMode || introduction) && (
                        <Introduction
                            {...addEditAttributes(
                                `${editAttributePrefix}Introduction`,
                            )}
                        >
                            {introduction}
                        </Introduction>
                    )}
                    <ContactInformation theme={theme}>
                        {(isInEditMode ||
                            hasLocation ||
                            showFindUsLink ||
                            phoneNumber) && (
                            <ContactInfoDetails>
                                {(isInEditMode ||
                                    hasLocation ||
                                    showFindUsLink) && (
                                    <ContactInfoCol>
                                        {l18n?.visitingAddress && (
                                            <DetailHeading>
                                                {l18n?.visitingAddress}
                                            </DetailHeading>
                                        )}
                                        {(isInEditMode ||
                                            location?.addressRow1) && (
                                            <div
                                                {...addEditAttributes(
                                                    `${editAttributePrefix}VisitingAddressRow1`,
                                                )}
                                            >
                                                {location?.addressRow1}
                                            </div>
                                        )}
                                        {(isInEditMode ||
                                            location?.addressRow2) && (
                                            <div
                                                {...addEditAttributes(
                                                    `${editAttributePrefix}VisitingAddressRow2`,
                                                )}
                                            >
                                                {location?.addressRow2}
                                            </div>
                                        )}
                                        <div>
                                            {(isInEditMode ||
                                                location?.postalCode) && (
                                                <span
                                                    {...addEditAttributes(
                                                        `${editAttributePrefix}VisitingAddressPostalCode`,
                                                    )}
                                                >
                                                    {location?.postalCode}{" "}
                                                </span>
                                            )}
                                            {(isInEditMode ||
                                                location?.city) && (
                                                <span
                                                    {...addEditAttributes(
                                                        `${editAttributePrefix}VisitingAddressCity`,
                                                    )}
                                                >
                                                    {location?.city}
                                                </span>
                                            )}
                                        </div>
                                        {showFindUsLink && (
                                            <FindUsLink
                                                to={findUsUrl}
                                                target="_blank"
                                                setTopMargin={!!hasLocation}
                                            >
                                                {l18n.findUs}
                                                <External icon="external28" />
                                            </FindUsLink>
                                        )}
                                    </ContactInfoCol>
                                )}

                                {(isInEditMode || phoneNumber || email) && (
                                    <ContactInfoCol>
                                        {(phoneNumber || isInEditMode) && (
                                            <ContactInfoDetailItem>
                                                {l18n?.telephone && (
                                                    <DetailHeading>
                                                        {l18n?.telephone}
                                                    </DetailHeading>
                                                )}
                                                <>
                                                    <StyledPhoneIcon
                                                        icon={`phone28`}
                                                        aria-hidden="true"
                                                    />
                                                    {isInEditMode ? (
                                                        <OpeLink
                                                            {...addEditAttributes(
                                                                `${editAttributePrefix}FrontOfficePhoneNumber`,
                                                            )}
                                                        >
                                                            {phoneNumber}
                                                        </OpeLink>
                                                    ) : (
                                                        <Link
                                                            to={`tel:${phoneNumber?.replace(
                                                                /\s+/g,
                                                                "",
                                                            )}`}
                                                        >
                                                            {phoneNumber}
                                                        </Link>
                                                    )}
                                                </>
                                            </ContactInfoDetailItem>
                                        )}
                                        {(email || isInEditMode) && (
                                            <ContactInfoDetailItem>
                                                {l18n?.email && (
                                                    <DetailHeading>
                                                        {l18n?.email}
                                                    </DetailHeading>
                                                )}
                                                <>
                                                    {isInEditMode ? (
                                                        <OpeLink
                                                            {...addEditAttributes(
                                                                `${editAttributePrefix}FrontOfficeEmailAdress`,
                                                            )}
                                                        >
                                                            {email}
                                                        </OpeLink>
                                                    ) : (
                                                        <Email
                                                            to={`mailto:${email?.replace(
                                                                /\s+/g,
                                                                "",
                                                            )}`}
                                                        >
                                                            {email}
                                                        </Email>
                                                    )}
                                                </>
                                            </ContactInfoDetailItem>
                                        )}
                                    </ContactInfoCol>
                                )}
                            </ContactInfoDetails>
                        )}
                    </ContactInformation>
                </OfficeInformation>
            </Section>
            <PageContent
                key={`Blocks`}
                areaName="Blocks"
                mainContent={blocks}
                componentSelector={getComponentTypeForContent}
            />
            {employeesByLetter && (
                <EmployeesByLetter
                    employeesByLetter={employeesByLetter}
                    heading={l18n?.contactUs || "Contact us"}
                    theme="lightgray"
                    anchorName={employeesAnchorName || "contact-us"}
                />
            )}
            {openPositions?.items && openPositions.items.length > 0 && (
                <OpenPositionListSection
                    theme="cyan"
                    contentTheme="blue"
                    displayOption="Right"
                    identifier={openPositions.identifier}
                    heading={openPositions.heading}
                    link={openPositions.link}
                    items={openPositions.items}
                    anchorName={openPositions.anchorName || "open-positions"}
                />
            )}
        </Wrapper>
    );
};

export default React.memo(OfficePage);
